import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
    Page,
    LayoutSingleColumn,
    LayoutWrapperTopbar,
    LayoutWrapperMain,
    LayoutWrapperFooter,
    CreditsPurchasingModal,
    AvailableUnlocksModal,
    Footer,
    NoResultsBlock,
    Button,
    IconSpinner,
    Alert,
    SecondaryButton,
} from '../../components';
import { TopbarContainer } from '../../containers';
import { ensureCurrentUser } from '../../util/data';
import { usePaymentStatus } from '../../hooks/usePaymentStatus';
import { revealUserVisitors } from '../../util/api';
import { stringifyToMarketplaceISO } from '../../util/dates';
import { isScrollingDisabled } from '../../ducks/UI.duck';
import { updateProfile } from '../ProfileSettingsPage/ProfileSettingsPage.duck';
import { fetchCurrentUser, updateUserProfileInfo } from '../../ducks/user.duck';
import config from '../../config';
import VisitorCard from './VisitorCard';
import VisitorsPageHeader from './VisitorsPageHeader';
import css from './VisitorsPage.css';
import { useVisitorsDataRequest } from './hooks/useVisitorsDataRequest';
import { RIDER_AVAILABILITY_NOT_AVAILABLE } from '../../marketplace-custom-config';
import { createResourceLocatorString } from '../../util/routes';
import routeConfiguration from '../../routeConfiguration';
import { LISTINGS_INITIAL_NUM, LISTINGS_NUM_PER_PAGE } from './VisitorsPage.helpers';

const { userTypeHorseowner, userTypeRider } = config;
const minCreditsNum = 10;

const schemaTitle = 'Deine Ansichten';
const schemaDescriptionRider = 'Hier siehst Du, wer vor Kurzem dein Profil angesehen hat.';
const schemaDescriptionHorseowner = 'Hier siehst Du, wer vor Kurzem deine Profile angesehen hat.';

export default () => {
    const history = useHistory();
    const dispatch = useDispatch();

    const onUpdateProfile = data => dispatch(updateProfile(data, {}));

    const scrollingDisabled = useSelector(state => isScrollingDisabled(state));
    const currentUser = useSelector(state => ensureCurrentUser(state.user.currentUser));
    const ownListings = useSelector(s => s.ManageListingsPage?.ownEntities?.ownListing);

    const [page, setPage] = useState(1);
    const [loadMoreAllowed, setLoadMoreAllowed] = useState(false);
    const [loading, setLoading] = useState(false);
    const [unlockModalOpen, setUnlockModalOpen] = useState(false);
    const [buyCreditsModalOpen, setBuyCreditsModalOpen] = useState(false);
    const [cardParams, setCardParams] = useState(null);
    const [visitors, setVisitors] = useState([]);
    const [initialRequestFulfilled, setInitialRequestFulfilled] = useState(false);
    const [revealInProgress, setRevealInProgress] = useState(false);

    const { paymentError, navigateToErrorPage, navigateFromErrorPage } = usePaymentStatus({
        location: history.location,
        history,
        pageName: 'VisitorsPage',
    });

    const {
        protectedData: { representationListingId },
        publicData: { userType, availabilityStatus },
        privateData: { creditsAvailable = {}, visitorsData = {}, visitorsData_lastVisitedAt = {} },
    } = currentUser.attributes.profile;

    const isRider = userType === userTypeRider;
    const isHorseowner = userType === userTypeHorseowner;

    const isNotAvailable = availabilityStatus === RIDER_AVAILABILITY_NOT_AVAILABLE;
    const riderNotAvailable = isRider && isNotAvailable;

    const ownerHasPublishedListings = Object.values(ownListings || {}).some(
        l => l.attributes.state === 'published'
    );
    const noAvailableListingsForOwner = isHorseowner && ownListings && !ownerHasPublishedListings;

    const creditsAvailableByType = Number(creditsAvailable[userType] || 0);
    const visitorsByType = !paymentError && visitorsData && visitorsData[userType];
    const insufficientCredits = creditsAvailableByType < minCreditsNum;
    const noVisitors = Array.isArray(visitorsByType) && visitorsByType.length === 0;
    const noResults =
        !visitorsByType ||
        noVisitors ||
        (initialRequestFulfilled && visitors.length === 0 && !paymentError);

    const schemaDescription = isHorseowner ? schemaDescriptionHorseowner : schemaDescriptionRider;
    const visitorsDataAvailable = !noResults && initialRequestFulfilled && !riderNotAvailable;

    const isNotAllowed = riderNotAvailable || noAvailableListingsForOwner;
    const isInitialRequest = visitors.length === 0;
    const perPage = isInitialRequest ? LISTINGS_INITIAL_NUM : LISTINGS_NUM_PER_PAGE;

    const addNewVisitors = (visitors = []) => {
        const loadMoreAllowed = visitors.length === perPage + 1;
        setLoadMoreAllowed(loadMoreAllowed);

        setVisitors(prevValue => [...prevValue, ...visitors.splice(0, perPage).filter(v => !!v)]);

        setInitialRequestFulfilled(true);

        setLoading(false);
    };

    useVisitorsDataRequest({
        page,
        perPage,
        representationListingId,
        visitorsByType: (visitorsByType || []).sort(
            (a, b) => new Date(b.visitedAt).getTime() - new Date(a.visitedAt).getTime()
        ),
        setLoading,
        callback: addNewVisitors,
    });

    useEffect(() => {
        if (!userType) return;

        onUpdateProfile({
            privateData: {
                visitorsData_lastVisitedAt: {
                    ...visitorsData_lastVisitedAt,
                    [userType]: stringifyToMarketplaceISO(new Date()),
                },
            },
        });
    }, [userType]);

    useEffect(() => {
        if (paymentError) {
            setVisitors([]);
        }
    }, [paymentError]);

    const revealVisitorCard = async () => {
        if (revealInProgress) return;

        setRevealInProgress(true);

        const { visitedEntryId, visitorRepresentationId } = cardParams;

        try {
            const response = await revealUserVisitors(visitedEntryId, visitorRepresentationId);
            const data = await response?.json();
            const { error, message, ...entityData } = data || {};

            if (error) {
                throw new Error(message);
            }

            const params = {
                privateData: {
                    visitorsData: {
                        [userType]: [...(visitorsByType || {})].map(v =>
                            v.visitedEntryId === visitedEntryId &&
                            v.visitorRepresentationId === visitorRepresentationId
                                ? {
                                      ...v,
                                      ...entityData,
                                  }
                                : v
                        ),
                    },
                },
            };

            await dispatch(updateUserProfileInfo(params));
            await dispatch(fetchCurrentUser());

            setVisitors(prevValue =>
                prevValue.map(prev =>
                    prev.visitedEntryId === visitedEntryId &&
                    prev.visitorRepresentationId === visitorRepresentationId
                        ? {
                              ...prev,
                              unlocked: true,
                          }
                        : prev
                )
            );
        } catch (error) {
            navigateToErrorPage(error.message);
        } finally {
            setRevealInProgress(false);
            setUnlockModalOpen(false);
        }
    };

    return (
        <Page
            scrollingDisabled={scrollingDisabled}
            contentType="website"
            description={schemaDescription}
            title={schemaTitle}
            schema={{
                '@context': 'http://schema.org',
                '@type': 'WebPage',
                description: schemaDescription,
                name: schemaTitle,
                image: ['https://horsedeal.imgix.net/static/social-image-1200x630.webp'],
            }}
        >
            <LayoutSingleColumn>
                <LayoutWrapperTopbar>
                    <TopbarContainer currentPage="VisitorsPage" />
                </LayoutWrapperTopbar>
                <LayoutWrapperMain
                    className={css.layout}
                    blobBackground
                    blobProps={{
                        xDistance: 20,
                        baseWidth: 760,
                    }}
                >
                    {paymentError && (
                        <main className={css.container}>
                            <p className={css.error}>
                                Zahlung fehlgeschlagen. Versuchen Sie es später oder wenden Sie sich
                                an das Supportteam.
                            </p>
                            <Button type="button" onClick={() => navigateFromErrorPage()}>
                                Zurück
                            </Button>
                        </main>
                    )}
                    {isNotAllowed && (
                        <main className={css.container}>
                            <VisitorsPageHeader
                                visitorsNum={(visitorsByType || []).length}
                                userType={userType}
                            />
                            <Alert
                                showAsPlainText
                                rootClassName={css.alert}
                                header="Ansichten unvollständig"
                                description={
                                    isRider
                                        ? 'Um alle Ansichten einsehen zu können, bestätige deine Verfügbarkeit.'
                                        : 'Um alle Ansichten einsehen zu können, reaktiviere deine Profile.'
                                }
                            >
                                <Button
                                    type="button"
                                    onClick={() =>
                                        history.push(
                                            createResourceLocatorString(
                                                isRider
                                                    ? 'ProfileSettingsPage'
                                                    : 'ManageListingsPage',
                                                routeConfiguration(),
                                                isRider ? { userType: userTypeRider } : {}
                                            )
                                        )
                                    }
                                >
                                    {isRider ? 'Mein Profil' : 'Meine Pferde'}
                                </Button>
                            </Alert>
                        </main>
                    )}
                    {!isNotAllowed && noResults && !paymentError && (
                        <NoResultsBlock
                            headerId="Du hast noch keine Ansichten."
                            parapraphId="Es scheint so, als hättest Du noch keine Ansichten erhalten."
                            searchFormPlaceholder="Suche in deiner Nähe"
                            rootClass={css.noResults}
                            isSearchForm
                        />
                    )}
                    {loading && (
                        <div className={css.loadingOverlay}>
                            <IconSpinner />
                        </div>
                    )}
                    {visitorsDataAvailable && (
                        <section className={css.container}>
                            <VisitorsPageHeader
                                visitorsNum={(visitorsByType || []).length}
                                userType={userType}
                            />
                            <div className={css.cardHolder}>
                                {visitors.map(visitor => (
                                    <VisitorCard
                                        key={`${visitor.visitedEntryId}-${visitor.visitorRepresentationId}-${visitor.unlocked}`}
                                        {...visitor}
                                        currentUser={currentUser}
                                        handleLockedCardClick={data => {
                                            if (insufficientCredits) {
                                                return setBuyCreditsModalOpen(true);
                                            }

                                            setUnlockModalOpen(true);
                                            setCardParams(data);
                                        }}
                                    />
                                ))}
                            </div>
                            {loadMoreAllowed && (
                                <SecondaryButton
                                    className={css.loadMoreButton}
                                    disabled={loading}
                                    inProgress={loading}
                                    onClick={() => setPage(prev => prev + 1)}
                                >
                                    Mehr anzeigen
                                </SecondaryButton>
                            )}
                        </section>
                    )}
                    {unlockModalOpen && (
                        <AvailableUnlocksModal
                            isOpen
                            currentUser={currentUser}
                            onClose={() => {
                                if (revealInProgress) return;

                                setUnlockModalOpen(false);
                            }}
                            handleAcceptOrDeclineRequest={revealVisitorCard}
                            inProgress={revealInProgress}
                        />
                    )}
                    {buyCreditsModalOpen && (
                        <CreditsPurchasingModal
                            isOpen
                            heading="Alles aufgebraucht!"
                            description="Füge mehr Credits hinzu, um diese Ansicht aufzudecken."
                            sidenote={<p>Wähle die Anzahl Credits</p>}
                            currentUser={currentUser}
                            onClose={() => setBuyCreditsModalOpen(false)}
                            onError={() => {
                                setBuyCreditsModalOpen(false);
                                navigateToErrorPage();
                            }}
                        />
                    )}
                </LayoutWrapperMain>
                <LayoutWrapperFooter>
                    <Footer />
                </LayoutWrapperFooter>
            </LayoutSingleColumn>
        </Page>
    );
};
